import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// Styles
import '../styles/SideBar.css'

// Services
import { getNumberProfilesForReview, getUnseenConversations, getById } from '../server/server'

// Contexts and Hooks
import { useSocket } from '../context/socket'
import { useUser } from '../auth'
import { useInstitution } from '../auth/InstitutionContext'
import { useAppContext } from '../context/AppContext'
import { isItAccess } from '../constants/Roles'

// Components
import LogoutModal from './modals/LogoutModal'
import SidebarItem from './SidebarItem'
import LanguageSelector from './LanguageSelector'
import SidebarLogo from './SidebarLogo'

// Utils and Constants
import i18n from '../i18n'
import { getLanguagePack } from '../utils/util'
import { sidebarDataPrimary, sidebarDataRegular } from '../constants/Constants'

// SVG Icons
import { ReactComponent as Dashboard } from '../assets/svg/dashboard.svg'
import { ReactComponent as Content } from '../assets/svg/content.svg'
import { ReactComponent as Classroom } from '../assets/svg/classroom.svg'
import { ReactComponent as Analyze } from '../assets/svg/analyze.svg'
import { ReactComponent as Chat } from '../assets/svg/chat.svg'
import { ReactComponent as Management } from '../assets/svg/management.svg'
import { ReactComponent as Wellbeing } from '../assets/svg/wellbeing.svg'
import { ReactComponent as Videos } from '../assets/svg/videos.svg'
import { ReactComponent as Settings } from '../assets/svg/settings.svg'
import { ReactComponent as Logout } from '../assets/svg/logout.svg'
import { ReactComponent as CatchUp } from '../assets/svg/catch-up.svg'
import { ReactComponent as Alerts } from '../assets/svg/alerts.svg'
import { ReactComponent as NavPin } from '../assets/svg/nav-pin.svg'

// Icon mapping for better maintainability
const ICON_COMPONENTS = {
    dashboard: Dashboard,
    content: Content,
    classroom: Classroom,
    analyze: Analyze,
    chat: Chat,
    management: Management,
    wellbeing: Wellbeing,
    videos: Videos,
    settings: Settings,
    logout: Logout,
    'catch-up': CatchUp,
    alerts: Alerts,
}

// The sidebar is expanded by default; a teacher can pin it closed and that choice sticks.
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed'
// Mirrors the `.nav-pin` hide rule in styles/SideBar.css. Below this width there is no pin
// control, so the rail keeps its old hover-only behaviour instead of stranding the teacher
// with a 280px sidebar they cannot close.
const PIN_AVAILABLE_QUERY = '(min-width: 1361px)'

// localStorage throws in Safari private mode, so every access is guarded.
const readStoredCollapsed = () => {
    try {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    } catch (error) {
        return false
    }
}

const writeStoredCollapsed = (value) => {
    try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value))
    } catch (error) {
        console.error('Failed to persist sidebar state:', error)
    }
}

const pinIsAvailable = () => (typeof window.matchMedia === 'function' ? window.matchMedia(PIN_AVAILABLE_QUERY).matches : true)

// Full-bleed child-facing screens. The rail is position: fixed and overlays their content,
// and the teacher chrome is both a distraction and a mis-tap risk for a 4-8 year old, so it
// rests collapsed there and the pin is hidden. Exact match, so /early/classroom/student-settings
// (a teacher screen sitting under the same prefix) is deliberately NOT included.
// Query strings live outside location.pathname, so /early/classroom?id=123 matches here.
const CHROME_FREE_PATHS = ['/early/classroom', '/early/classroom/student-select-emotion']

const isChromeFreeRoute = (pathname = '') => CHROME_FREE_PATHS.includes(pathname.replace(/\/+$/, '') || '/')

function SideBar() {
    // Router hooks
    const location = useLocation()
    const history = useHistory()

    // Context hooks
    const { user, setUser } = useUser()
    const { appState, setAppState } = useAppContext()
    const { institution, setInstitution } = useInstitution()
    const { socketStates, changeSocketState } = useSocket()
    const { t } = useTranslation()

    // Local state
    const [homeStatus, setHomeStatus] = useState('/dashboard')
    const [selectedLang, setSelectedLanguage] = useState(i18n.language)
    const [pinnedClosed, setPinnedClosed] = useState(readStoredCollapsed)
    const [pinAvailable, setPinAvailable] = useState(pinIsAvailable)
    // Width of the rail at rest. Hovering expands it temporarily; this is what it returns to.
    // The route override is never persisted: the teacher's stored preference is left untouched
    // and applies again by itself the moment they navigate off these screens.
    const chromeFree = isChromeFreeRoute(location.pathname)
    const showPin = pinAvailable && !chromeFree
    const restCollapsed = chromeFree ? true : pinAvailable ? pinnedClosed : true
    const [collapsed, setCollapsed] = useState(restCollapsed)
    const [logoutModalOpen, setLogoutModalOpen] = useState(false)
    const [showContent, setShowContent] = useState(false)
    const [showAlerts, setShowAlerts] = useState(false)

    // Computed values
    const numberOfUnseenConversations = socketStates?.unseenConversations?.length || 0
    const primaryUI = !!appState?.primary
    const isAdmin = user?.role === 'school_admin' || user?.role_coverage?.target_text === 'All students'
    const langs = useMemo(() => getLanguagePack(user?.institution_id), [user?.institution_id])
    const uiModeLabel = appState.primary ? t('Open Upstrive Education') : t('Open Upstrive Early')

    // Sidebar data based on UI mode
    const sidebarData = useMemo(
        () => (primaryUI ? sidebarDataPrimary() : sidebarDataRegular(numberOfUnseenConversations, isAdmin)),
        [primaryUI, numberOfUnseenConversations, isAdmin]
    )

    // Effects
    useEffect(() => {
        const pathList = location.pathname.split('/').slice(1)
        const shortenedPath = '/' + pathList.join('/')
        setHomeStatus(shortenedPath)
    }, [location.pathname])

    // API calls
    const loadUnseenConversations = async () => {
        try {
            const response = await getUnseenConversations()
            if (response.status === 200) {
                changeSocketState({ unseenConversations: response?.data || [] })
            }
        } catch (error) {
            console.error('Failed to load unseen conversations:', error)
        }
    }

    const loadProfilesForReview = async () => {
        try {
            const profiles = await getNumberProfilesForReview()
            if (profiles.status === 200) {
                // Store profiles if needed for future use
                console.log('Profiles loaded:', profiles.data)
            }
        } catch (error) {
            console.error('Failed to load profiles for review:', error)
        }
    }

    const loadInstitution = async () => {
        try {
            const resp = await getById({ collection: 'institutions', id: user.institution_id })
            if (resp.status === 200) {
                setInstitution(resp?.data)
            }
        } catch (error) {
            console.error('Failed to load institution:', error)
        }
    }

    // Initialize data on mount
    useEffect(() => {
        // IT Access has no chat or wellbeing-review features; skip those calls
        // so the sidebar doesn't fire forbidden (403) requests on load.
        if (!isItAccess(user)) {
            loadUnseenConversations()
            loadProfilesForReview()
        }
        loadInstitution()
    }, [])

    // Update document title based on unseen conversations
    useEffect(() => {
        const unseenCount = socketStates?.unseenConversations?.length
        const currentTitle = document.title
        const baseTitleMatch = currentTitle.match(/\)\s*(.+)$/)
        const baseTitle = baseTitleMatch ? baseTitleMatch[1] : currentTitle

        document.title = unseenCount ? `(${unseenCount}) ${baseTitle}` : baseTitle
    }, [socketStates?.unseenConversations?.length])

    // Track whether the pin control is on screen; below the breakpoint the preference is ignored.
    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined

        const mediaQuery = window.matchMedia(PIN_AVAILABLE_QUERY)
        const handleChange = (event) => setPinAvailable(event.matches)

        // Safari < 14 only implements the deprecated addListener/removeListener pair.
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange)
        } else {
            mediaQuery.addListener(handleChange)
        }

        // The breakpoint may have been crossed between the initial render and this effect.
        setPinAvailable(mediaQuery.matches)

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleChange)
            } else {
                mediaQuery.removeListener(handleChange)
            }
        }
    }, [])

    // Re-sync the rail whenever the resting width changes (pin toggled, breakpoint crossed).
    useEffect(() => {
        setCollapsed(restCollapsed)
    }, [restCollapsed])

    // Update main container margin to match the rail. useLayoutEffect, not useEffect: the
    // static `.home-container` rule is margin-left: 80px, so deferring this would paint the
    // narrow margin first and jump to 280px on every load now that expanded is the default.
    useLayoutEffect(() => {
        const homeContainer = document.getElementById('home-container')
        if (homeContainer) {
            homeContainer.style.marginLeft = restCollapsed ? '80px' : '280px'
        }
    }, [restCollapsed])

    // Update root class so the responsive widget compensations follow the rail
    useLayoutEffect(() => {
        const rootElement = document.getElementById('root')
        if (rootElement) {
            rootElement.classList.toggle('sidebarPinned', !restCollapsed)
        }
    }, [restCollapsed])

    // Update selected language when i18n language changes
    useEffect(() => {
        setSelectedLanguage(i18n.language)
    }, [appState.locale])

    // Render icon component based on name
    const renderIcon = (name, color) => {
        const IconComponent = ICON_COMPONENTS[name] || Dashboard
        // The surrounding link/button carries the accessible name, so the glyph is decorative.
        const commonProps = { fill: color, 'aria-hidden': true, focusable: false }

        // Special case for alerts (uses stroke instead of fill)
        if (name === 'alerts') {
            return <IconComponent stroke={color} aria-hidden="true" focusable="false" />
        }

        // Special case for dashboard (has specific dimensions)
        if (name === 'dashboard') {
            return <IconComponent {...commonProps} height={17} width={16} />
        }

        return <IconComponent {...commonProps} />
    }

    // Handle language change
    const handleLangChange = (code) => {
        setSelectedLanguage(code)
        i18n.changeLanguage(code)
        localStorage.setItem('locale', code)
        setAppState((prev) => ({ ...prev, locale: code }))
    }

    // Modal handlers
    const openLogoutModal = useCallback(() => setLogoutModalOpen(true), [])
    const closeModal = useCallback(() => setLogoutModalOpen(false), [])

    // Pin handler — the pin closes the sidebar and keeps it closed across sessions
    const togglePin = () => {
        const next = !pinnedClosed
        setPinnedClosed(next)
        setCollapsed(next)
        writeStoredCollapsed(next)
    }

    // Logout handler
    const logout = () => {
        // The sidebar preference is a device setting, not session data — carry it across
        // the clear() so a teacher's pinned-closed rail survives signing out and back in.
        let storedCollapsed = null
        try {
            storedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
        } catch (error) {
            storedCollapsed = null
        }

        localStorage.clear()

        if (storedCollapsed !== null) {
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, storedCollapsed)
            } catch (error) {
                console.error('Failed to persist sidebar state:', error)
            }
        }

        setUser({})
        setAppState({})
        history.push('/')
        setLogoutModalOpen(false)
    }

    // UI mode toggle handler
    const handleUIModeToggle = () => {
        const nextState = !appState?.primary
        setAppState({ ...appState, primary: nextState })
        localStorage.setItem('uiMode', nextState ? 'primary' : 'regular')
        history.push('/dashboard')
    }

    // Dropdown toggle handlers
    const handleDropdownToggle = useCallback((item) => {
        setShowContent(false)
        setShowAlerts(false)

        if (item.title === 'content') {
            setShowContent((prev) => !prev)
        }
        if (item.title === 'Alerts') {
            setShowAlerts((prev) => !prev)
        }
    }, [])

    // Check if dropdown is open
    const isDropdownOpen = useCallback(
        (item) => {
            if (item.title === 'content' && showContent) return true
            if (item.title === 'Alerts' && showAlerts) return true
            return false
        },
        [showContent, showAlerts]
    )

    return (
        <div
            id="sideBar"
            className={`side-bar bg-deepPurple ${collapsed ? 'side-bar-collapsed' : 'side-bar-open'}`}
            style={collapsed ? { width: 80 } : {}}
            onMouseLeave={() => {
                setShowContent(false)
                setShowAlerts(false)
            }}
        >
            <div
                className="flex flex-col items-center h-[100vh] w-full"
                onMouseLeave={() => {
                    setCollapsed(restCollapsed)
                }}
                onMouseEnter={() => setCollapsed(false)}
            >
                {showPin && (
                    <button
                        type="button"
                        className="nav-pin bg-deepPurple"
                        onClick={togglePin}
                        aria-pressed={!restCollapsed}
                        aria-label={pinnedClosed ? t('expandSidebar') : t('collapseSidebar')}
                        title={pinnedClosed ? t('expandSidebar') : t('collapseSidebar')}
                    >
                        {/* Yellow means the rail is held open. That is the inverse of `pinnedClosed`,
                            which is named for the rail state (true = 80px rail), not for whether the
                            toggle reads as "on". Driving the fill off restCollapsed keeps the pin
                            truthful if anything other than the preference forces the rail closed. */}
                        <NavPin fill={restCollapsed ? '#F8F8FB' : '#FDDF6D'} style={{ display: 'block' }} aria-hidden="true" focusable="false" />
                    </button>
                )}
                <SidebarLogo collapsed={collapsed} primaryUI={primaryUI} />
                <div className="my-[5px] min-h-6 min-w-6">
                    <span
                        className={`px-[30px] text-[#F8F8FB] text-[16px] font-bold w-full ellipsis max-w-[270px] text-ellipsis ${collapsed ? 'opacity-0' : 'opacity-100'}`}
                        style={{ display: collapsed ? 'none' : 'block' }}
                    >
                        {institution.name}
                    </span>
                </div>
                <LanguageSelector langs={langs} selectedLang={selectedLang} collapsed={collapsed} onLanguageChange={handleLangChange} />
                <div className="mt-1 xl3:mt-6 w-full flex flex-col pl-[30px] overflow-y-auto overflow-x-hidden">
                    <nav aria-label={t('mainNavigation')} className=" text-[#F8F8FB] font-bold text-lg flex flex-col justify-between h-[100vh]">
                        <div className="link-list">
                            {sidebarData?.map((item, index) => {
                                if (item.name === 'videos' && i18n.language !== 'en') return null
                                if (user.hasOnlyPrimaryStudents && ['content', 'chat'].includes(item.name)) return null
                                // IT Access is restricted to User Management; hide every other nav item.
                                if (isItAccess(user) && item.name !== 'management') return null

                                return (
                                    <SidebarItem
                                        key={item?.path[0]}
                                        item={item}
                                        homeStatus={homeStatus}
                                        renderIcon={renderIcon}
                                        collapsed={collapsed}
                                        setHomeStatus={setHomeStatus}
                                        isOpen={isDropdownOpen(item)}
                                        onToggle={handleDropdownToggle}
                                    />
                                )
                            })}
                        </div>
                        <div className="bottom-container">
                            {!!user?.hasPrimaryClasses && !user.hasOnlyPrimaryStudents && (
                                <div id={`sidebar-navigation-button-upstrive-early`} className=" nav-text cursor-pointer">
                                    <button
                                        type="button"
                                        className={`h-[45px] 2xl:h-[52px] flex flex-row  items-center relative transition-[height] w-full text-left`}
                                        onClick={handleUIModeToggle}
                                        aria-label={uiModeLabel}
                                        title={uiModeLabel}
                                    >
                                        <div className="items-center">{renderIcon('switch', '#F8F8FB')}</div>
                                        {!collapsed && <span className="px-3 w-full ellipsis text-ellipsis">{uiModeLabel}</span>}
                                    </button>
                                </div>
                            )}
                            <div
                                className=" cursor-pointer nav-text"
                                onClick={() => {
                                    setHomeStatus('/settings')
                                }}
                            >
                                <Link to="/settings" aria-label={t('settings')} title={t('settings')}>
                                    <div id="sidebar-navigation-button-settings" className={`h-[52px] flex flex-row  items-center relative w-full cursor-pointer`}>
                                        <div className="items-center">{renderIcon('settings', homeStatus === '/settings' ? '#FDDF6D' : '#F8F8FB')}</div>
                                        {!collapsed && <span className={`px-3 ${homeStatus === '/settings' ? ' text-highlightColor' : ''}`}>{t('settings')}</span>}
                                    </div>
                                </Link>
                            </div>
                            <div className=" nav-text cursor-pointer">
                                <button type="button" className={`h-[52px] flex flex-row  items-center relative w-full text-left`} onClick={() => openLogoutModal()} aria-label={t('logout')} title={t('logout')}>
                                    <div id="logout-button" className="items-center">
                                        {renderIcon('logout', '#F8F8FB')}
                                    </div>
                                    {!collapsed && <span className="px-3 w-full ellipsis text-ellipsis">{t('logout')}</span>}
                                </button>
                            </div>
                        </div>
                    </nav>
                </div>
            </div>

            <LogoutModal modalIsOpen={logoutModalOpen} closeModal={closeModal} logout={logout} />
        </div>
    )
}

export default SideBar
